import React, { useEffect, useState } from "react";
import { getFirestore, doc, getDoc, collection, query, where, getDocs } from "firebase/firestore"; // Added query and getDocs
import { getAuth, onAuthStateChanged } from "firebase/auth";
import TreasureMap from "../components/TreasureMap";
import { useParams, useNavigate } from "react-router-dom"; // Added useNavigate
import { motion } from "framer-motion"; // Added motion import
import { stateTopicsMap } from "../data/stateTopicsMap";
import va1ScienceTopics from "../data/va-topics/va-1st-educator-science-topics";


const MapPage = () => {
  const { slug, subject } = useParams();  // 👈 Now subject comes from URL
  const [completedTopics, setCompletedTopics] = useState([]);
  const [visibleTopics, setVisibleTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [userGrade, setUserGrade] = useState("1st"); // Added userGrade state
  const [userState, setUserState] = useState("nc");  // Added userState state
  const [classTitle, setClassTitle] = useState(""); // Added classTitle state
  const [userDoc, setUserDoc] = useState(null); // Add state for user document
  const [subjectKey, setSubjectKey] = useState("");

  const db = getFirestore();
  const auth = getAuth();
  const navigate = useNavigate(); // Added useNavigate hook

  const mergedTopicsMapBySubject = Object.values(stateTopicsMap).reduce((acc, cur) => ({
    ...acc,
    ...cur
  }), {});

  async function getEducatorDocForUser(db, userData) {
    // If the user is an educator, they control visibility
    if (userData?.role?.includes?.("educator")) {
      return { ...userData, isEducator: true };
    }
  
    // If the user is a student with an educatorCode, find that educator
    if (userData?.role?.includes?.("student") && userData.educatorCode) {
      const state = (userData.state || "nc").toLowerCase();
      const grade = (userData.grade || "1st").toLowerCase();
  
      const roleCandidates = [
        `${state}-${grade}-educator`,
        "va-1st-educator",
        "educator",
      ];
  
      // 1) Try role as ARRAY (array-contains-any)
      try {
        const qArray = query(
          collection(db, "users"),
          where("educatorCode", "==", userData.educatorCode),
          where("role", "array-contains-any", roleCandidates)
        );
        const snapArray = await getDocs(qArray);
        if (!snapArray.empty) return { ...snapArray.docs[0].data(), isEducator: false };
      } catch (e) {
        console.warn("array-contains-any query failed (likely role not array):", e);
      }
  
      // 2) Try role as STRING (in)
      try {
        const qString = query(
          collection(db, "users"),
          where("educatorCode", "==", userData.educatorCode),
          where("role", "in", roleCandidates)
        );
        const snapString = await getDocs(qString);
        if (!snapString.empty) return { ...snapString.docs[0].data(), isEducator: false };
      } catch (e) {
        console.warn("in query failed:", e);
      }
    }
  
    return null;
  }
  

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          setUserDoc(userSnap.data()); // Store user document in state
          setCompletedTopics(userSnap.data().completedTopics || []);

          const { firstName, lastName, sirName, educatorCode, grade, state, role } = userSnap.data();

          let readableState = (state || "NC").toUpperCase();
          let readableGrade = grade?.toLowerCase()
            .replace("1st", "1st Grade")
            .replace("2nd", "2nd Grade");
          let subjectTitle = subject.charAt(0).toUpperCase() + subject.slice(1);

          let displayTitle = `${readableState} ${readableGrade} ${subjectTitle}`;

          // If educator
          if (role?.includes("educator")) {
            displayTitle += ` – ${sirName || ""} ${lastName}'s Class`;
          }
          // If student with educatorCode
          else if (role?.includes("student") && educatorCode) {
            const educatorQuery = query(
              collection(db, "users"),
              where("educatorCode", "==", educatorCode),
              where("role", "array-contains-any", [`${state}-${grade}-educator`, "educator"])
            );
            const educatorSnapshot = await getDocs(educatorQuery);
            if (!educatorSnapshot.empty) {
              const educator = educatorSnapshot.docs[0].data();
              displayTitle += ` – ${educator.sirName || ""} ${educator.lastName}'s Class`;
            } else {
              displayTitle = `Welcome, ${firstName} – ${displayTitle}`;
            }
          }
          // If student without educator
          else if (role?.includes("student")) {
            displayTitle = `Welcome, ${firstName} – ${displayTitle}`;
          }

          setClassTitle(displayTitle);

          let rawState = state?.toLowerCase() || "nc";
          let rawGrade = grade?.toLowerCase() || "1st";
          
          // Clean values
          let resolvedState = rawState.split(",")[0].trim(); // Renamed to resolvedState
          let resolvedGrade = rawGrade.split(",")[0].trim(); // Renamed to resolvedGrade
          
          // Validate
          const validStates = ["al","ak","az","ar","ca","co","ct","de","fl","ga","hi","id","il","in","ia","ks","ky","la","me","md","ma","mi","mn","ms","mo","mt","ne","nv","nh","nj","nm","ny","nc","nd","oh","ok","or","pa","ri","sc","sd","tn","tx","ut","vt","va","wa","wv","wi","wy","dc"];
          const validGrades = ["1st", "2nd", "3rd", "4th", "5th"];
          
          let stateIsValid = validStates.includes(resolvedState);
          let gradeIsValid = validGrades.includes(resolvedGrade);
          
          // 🔁 Force overwrite from slug if state or grade is invalid
          if (!stateIsValid || !gradeIsValid) {
            const slugParts = slug?.split("-");
            if (slugParts?.length >= 2) {
              const parsedState = slugParts[0]?.toLowerCase();
              const parsedGrade = slugParts[1]?.toLowerCase();
              if (validStates.includes(parsedState)) {
                resolvedState = parsedState;
                stateIsValid = true;
              }
              if (validGrades.includes(parsedGrade)) {
                resolvedGrade = parsedGrade;
                gradeIsValid = true;
              }
            }
          }
          
          // Final fallback if STILL invalid
          if (!stateIsValid) resolvedState = "nc";
          if (!gradeIsValid) resolvedGrade = "1st";

          // Store resolved state and grade
          setUserState(resolvedState);
          setUserGrade(resolvedGrade);

          setSubjectKey(`${resolvedState}_${resolvedGrade}_${subject.toLowerCase()}`); // Set subjectKey

          const subjectKey = `${resolvedState}_${resolvedGrade}_${subject.toLowerCase()}`; // Added subjectKey definition

          const key = `${resolvedState}_${resolvedGrade}_${subject.toLowerCase()}`;
          let selected = mergedTopicsMapBySubject[key] || [];

          // 🔁 Fallback: if stateTopicsMap has nothing, use your static VA 1st Science list
          if (!Array.isArray(selected) || selected.length === 0) {
            selected = Array.isArray(va1ScienceTopics) ? va1ScienceTopics : [];
          }

          setSelectedTopics(selected);

          console.log("[MapPage] key:", key);
          console.log("[MapPage] selectedTopics:", selected.map(t => t.slug));
          
          console.log("[MapPage] visibleTopics len/alive:", selected.length, " / ",
            (Array.isArray(visibleTopics) ? visibleTopics.length : 0));

          try {
            const owner = await getEducatorDocForUser(db, userSnap.data());

            const toolsRaw = Array.isArray(owner?.scitools) ? owner.scitools : [];

            // keep only this subject if you store per-subject flags
            const currentSubject = String(subject || "").toLowerCase().trim();
            const tools = toolsRaw.filter(
                 t => !t.subject || String(t.subject).toLowerCase() === currentSubject
               );

            console.log("[MapPage] educator tools:", tools.length, tools);

            // Map by slug (preferred). Default hidden unless explicitly visible.
            // ✅ Prefer slug mapping
const visibleBySlug = new Map(
  tools
    .filter(t => t?.slug)
    .map(t => [String(t.slug).trim().toLowerCase(), (t.visible ?? true)])
);

// ✅ Legacy fallback: title mapping
const visibleByTitle = new Map(
  tools
    .filter(t => t?.title)
    .map(t => [String(t.title).trim().toLowerCase(), (t.visible ?? true)])
);

// If educator has at least one visibility rule, default others to HIDDEN.
// If there are no rules at all, default to SHOW ALL (nice for first run).
const hasAnyVisibilityRules = tools.length > 0;

let aligned = selected.map(topic => {
  const slugKey = String(topic?.slug || "").trim().toLowerCase();
  if (visibleBySlug.has(slugKey)) return !!visibleBySlug.get(slugKey);

  const titleKey = String(topic?.title || "").trim().toLowerCase();
  if (visibleByTitle.has(titleKey)) return !!visibleByTitle.get(titleKey);

  // ⬇️ The key behavior change:
  return hasAnyVisibilityRules ? false : true;
});

console.log("[MapPage] hasAnyVisibilityRules:", hasAnyVisibilityRules);
console.log("[MapPage] aligned true count:", aligned.filter(Boolean).length, "/", aligned.length);

// 🛡️ Safety: lengths must match and we should not pass an empty array while UI loads
if (!aligned.length || aligned.length !== selected.length) {
  aligned = selected.map(() => true);
}

setVisibleTopics(aligned);

            // (Optional) quick diag:
            console.debug(
              "[MapPage] selected:", selected.length,
              "checked:", aligned.filter(Boolean).length,
              "subject:", currentSubject,
              "visible:", aligned.filter(Boolean).length,
              "subjectToolsUsed:", tools.length
            );
          } catch (e) {
            console.warn("Failed to load educator visibility; defaulting to all visible.", e);
            setVisibleTopics(selected.map(() => true));
          }
        }

        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [auth, db, subject]); // Ensure `subject` is included in the dependency array

  const refreshProgress = async (userId) => {
    const userRef = doc(db, "users", userId);
    const userSnap = await getDoc(userRef);
    if (userSnap.exists()) {
      setCompletedTopics(userSnap.data().completedTopics || []);
    }
  };

  if (loading) {
    return <p>Loading map data...</p>;
  }

  return (
    <div style={styles.mapWrapper}>
      <div style={styles.returnButtonWrapper}>
      <motion.img
  src="/assets/returntotopics_button.png"
  alt="Return to Topics"
  onClick={() => {
    const path = buildReturnPath({
      userDoc, // Use the stored user document
      subject,
      fallbackState: userState,
      fallbackGrade: userGrade,
    });
    navigate(path);
  }}
  whileHover={{ scale: 1.1 }}
  transition={{ type: "spring", stiffness: 300 }}
  style={styles.returnButtonImage}
/>
      </div>

      <h1 style={styles.classTitle}>{classTitle}</h1> {/* Added class title display */}

      <TreasureMap
        topics={selectedTopics}
        visibleTopics={visibleTopics}
        completedTopics={completedTopics}
        targetSlug={slug}
        subject={subject}
        classTitle={classTitle}
        subjectKey={subjectKey}
        refreshProgress={refreshProgress} // ✅ Pass refreshProgress to TreasureMap
        onProgressUpdate={refreshProgress} // ✅ Pass refreshProgress as onProgressUpdate
      />
    </div>
  );
};

// Helper: build correct "back" path for student/educator
function buildReturnPath({ userDoc, subject, fallbackState, fallbackGrade }) {
  const subjectLower = String(subject || "").toLowerCase();

  // role can be a string or an array; normalize
  const roleRaw = userDoc?.role;
  const roles = Array.isArray(roleRaw) ? roleRaw : [roleRaw].filter(Boolean);

  const state = (userDoc?.state || fallbackState || "nc").toLowerCase();
  // grades sometimes come like "1st, something" -> take first token
  const grade = (userDoc?.grade || fallbackGrade || "1st").split(",")[0].trim().toLowerCase();

  const isEducator = roles.some(r => String(r).toLowerCase().includes("educator"));
  const isStudent  = roles.some(r => String(r).toLowerCase().includes("student"));

  if (isStudent) {
    // e.g. /student/va/va-1st-student-science
    return `/student/${state}/${state}-${grade}-student-${subjectLower}`;
  }

  if (isEducator) {
    // e.g. /educator/nc/nc-3rd-educator-science
    return `/educator/${state}/${state}-${grade}-educator-${subjectLower}`;
  }

  // Fallback: generic topics grid
  return `/topics/${state}-${grade}-${subjectLower}`;
}

// Styles for the MapPage
const styles = {
  mapWrapper: {
    position: "relative",
    backgroundImage: `url("/assets/map-background.jpg")`,
    backgroundSize: "cover",
    minHeight: "100vh",
    paddingTop: "20px",
  },
  returnButtonWrapper: {
    position: "absolute",
    top: 20,
    left: 20,
    zIndex: 10,
  },
  returnButtonImage: {
    width: 160,
    height: "auto",
    cursor: "pointer",
  },
  classTitle: {
    textAlign: "center",
    fontSize: "24px",
    color: "#fff",
    marginTop: "20px",
  },
};

export default MapPage;
